import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table } from "react-bootstrap";
import { ClickButton } from "../components/Buttons";
import { Calendernew } from "../components/Forms";
import API_DOMAIN from "../config/config";
import { PDFDownloadLink } from "@react-pdf/renderer";
import AttendancePDF from "./AttendancePDF";

const AttendanceReport = () => {
  const [formData, setFormData] = useState({ fromdate: "", todate: "" });

  console.log("formdata", formData);
  const [allAttendance, setAllAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [staffSummary, setStaffSummary] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all attendance records once
  const fetchAllAttendance = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_DOMAIN}/attendance.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "listAttendance" }),
      });

      if (!response.ok) throw new Error("Network error");

      const res = await response.json();
      if (res.head.code !== 200)
        throw new Error(res.head.msg || "Failed to load");

      const transformed = res.body.attendance.map((item) => ({
        date: item.entry_date, // YYYY-MM-DD
        dateObj: new Date(item.entry_date),
        data: item.data || [],
      }));

      setAllAttendance(transformed);
      setFilteredAttendance(transformed);
    } catch (error) {
      console.error("Error fetching attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllAttendance();
  }, []);

  // Filter by date range
  useEffect(() => {
    let filtered = allAttendance;

    if (formData.fromdate) {
      const from = new Date(formData.fromdate);
      from.setHours(0, 0, 0, 0);
      filtered = filtered.filter((item) => item.dateObj >= from);
    }

    if (formData.todate) {
      const to = new Date(formData.todate);
      to.setHours(23, 59, 59, 999);
      filtered = filtered.filter((item) => item.dateObj <= to);
    }

    setFilteredAttendance(filtered);
  }, [formData.fromdate, formData.todate, allAttendance]);

  // Generate Staff-wise Summary
  useEffect(() => {
    const summaryMap = {};

    filteredAttendance.forEach((day) => {
      day.data.forEach((record) => {
        const name = record.staff_name;
        const fh = record.firstHalf || "absent";
        const sh = record.secondHalf || "absent";
        const wage = parseFloat(record.wages) || 0;

        if (!summaryMap[name]) {
          summaryMap[name] = {
            staffName: name,
            fullDays: 0,
            halfDays: 0, // stored as 0.5 per half-day
            presentDays: 0, // full + half
          };
        }

        if (fh === "present" && sh === "present") {
          summaryMap[name].fullDays += 1;
          summaryMap[name].presentDays += 1;
          summaryMap[name].totalSalary =
            (summaryMap[name].totalSalary || 0) + wage;
        } else if (fh === "present" || sh === "present") {
          summaryMap[name].halfDays += 0.5;
          summaryMap[name].presentDays += 0.5;
          summaryMap[name].totalSalary =
            (summaryMap[name].totalSalary || 0) + wage;
        } else if (fh === "absent" && sh === "absent") {
          // No salary added for full absent
        }
      });
    });

    const summaryArray = Object.values(summaryMap)
      .map((staff) => ({
        ...staff,
       absentDays: Number(
      (totalDaysInRange - staff.presentDays).toFixed(1)
    ),

        totalSalary: Number((staff.totalSalary || 0).toFixed(2)),
      }))
      .sort((a, b) => a.staffName.localeCompare(b.staffName));

    setStaffSummary(summaryArray);
  }, [filteredAttendance]);

  const getTotalDaysBetween = (from, to) => {
    if (!from || !to) return 0;

    const start = new Date(from);
    const end = new Date(to);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const diffTime = end - start;
    return diffTime / (1000 * 60 * 60 * 24) + 1; // inclusive
  };

  const handleDateChange = (date, field) => {
    const formatted = date ? date.toISOString().split("T")[0] : "";
    setFormData((prev) => ({ ...prev, [field]: formatted }));
  };

  const totalDaysInRange = getTotalDaysBetween(
    formData.fromdate,
    formData.todate
  );

  return (
    <div className="main my-4">
      <Container>
        {/* Header */}
        <Row className="align-items-center mb-4">
          <Col xs={12} md={6}>
            <h4 className="mb-0 fw-bold text-primary">Attendance Report</h4>
            {formData.fromdate && formData.todate && (
              <small className="text-muted">
                {new Date(formData.fromdate).toLocaleDateString("en-IN")} →{" "}
                {new Date(formData.todate).toLocaleDateString("en-IN")}(
                {totalDaysInRange} days)
              </small>
            )}
          </Col>
          <Col xs={12} md={6} className="text-md-end mt-3 mt-md-0">
            {staffSummary.length > 0 && (
              <PDFDownloadLink
                document={
                  <AttendancePDF
                    data={staffSummary}
                    fromDate={formData.fromdate} // Changed from 'from' to 'fromDate'
                    toDate={formData.todate} // Changed from 'to' to 'toDate'
                    totalDays={totalDaysInRange}
                  />
                }
                fileName={`Attendance_Report_${
                  formData.fromdate || "start"
                }_to_${formData.todate || "end"}.pdf`}
              >
                {({ loading }) => (
                  <ClickButton
                    label={
                      loading ? "Generating PDF..." : "Download PDF Report"
                    }
                    className="px-4"
                  />
                )}
              </PDFDownloadLink>
            )}
          </Col>
        </Row>

        {/* Date Pickers */}
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6}>
            <Calendernew
              selectedDate={
                formData.fromdate ? new Date(formData.fromdate) : null
              }
              onDateChange={(date) => handleDateChange(date, "fromdate")}
              calenderlabel="From Date"
              placeholder="Select start date"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Calendernew
              selectedDate={formData.todate ? new Date(formData.todate) : null}
              onDateChange={(date) => handleDateChange(date, "todate")}
              calenderlabel="To Date"
              placeholder="Select end date"
            />
          </Col>
        </Row>

        {/* Summary Table */}
        <div className="table-responsive shadow-sm rounded overflow-hidden border">
          <Table bordered hover className="mb-0 table-striped">
            <thead className="table-dark text-white text-center">
              <tr>
                <th>S.No</th>
                <th>Staff Name</th>
                <th>Full Days</th>
                <th>Half Days</th>
                <th>Present Days</th>
                <th>Absent Days</th>
                <th>Total Salary</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <span>Loading attendance records...</span>
                  </td>
                </tr>
              ) : staffSummary.length > 0 ? (
                staffSummary.map((staff, i) => (
                  <tr key={i}>
                    <td className="text-center">{i + 1}</td>
                    <td className="fw-bold">{staff.staffName}</td>
                    <td className="text-center text-success">
                      {staff.fullDays}
                    </td>
                    <td className="text-center text-warning">
                      {staff.halfDays}
                    </td>
                    <td className="text-center fw-bold text-primary">
                      {staff.presentDays}
                    </td>
                    <td className="text-center text-danger">
                      {staff.absentDays}
                    </td>
                    <td className="text-end fw-bold text-success">
                      ₹{staff.totalSalary}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    {formData.fromdate || formData.todate
                      ? "No attendance records found in selected date range"
                      : "Please select date range to view report"}
                  </td>
                </tr>
              )}
            </tbody>
            {staffSummary.length > 0 && (
              <tfoot className="table-light fw-bold">
                <tr>
                  <td colSpan="2" className="text-end">
                    TOTAL
                  </td>
                  <td className="text-center">
                    {staffSummary.reduce((sum, s) => sum + s.fullDays, 0)}
                  </td>
                  <td className="text-center">
                    {staffSummary.reduce((sum, s) => sum + s.halfDays, 0)}
                  </td>
                  <td className="text-center">
                    {staffSummary
                      .reduce((sum, s) => sum + parseFloat(s.presentDays), 0)
                      .toFixed(1)}
                  </td>
                  <td className="text-center">
                    {staffSummary
                      .reduce((sum, s) => sum + parseFloat(s.absentDays), 0)
                      .toFixed(1)}
                  </td>
                  <td className="text-end text-success">
                    ₹
                    {staffSummary
                      .reduce((sum, s) => sum + parseFloat(s.totalSalary), 0)
                      .toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </div>

        {/* Mobile Hint */}
        <div className="d-block d-lg-none text-center mt-3 text-muted small">
          ← Swipe to see more columns →
        </div>
      </Container>
    </div>
  );
};

export default AttendanceReport;
